import { useCallback, useEffect, useState } from "react";
import axiosInstance from "../../../api/axiosInstance";
import BlogEditor, { isEmptyHtml } from "./BlogEditor";
import BlogSeoSchema, { EMPTY_SEO, EMPTY_SCHEMA, mergeSeo, mergeSchema, canonicalFor, checkSocialImage,resolveSeo } from "./BlogSeoSchema"

export const BLOG_ROUTE = "/cms/blog-page";
export const BLOG_NEW_ROUTE = "/cms/blog-page/create";
export const blogEditRoute = (slug) => `/cms/blog-page/edit/${slug}`;

/* ---------------------------- API layer ---------------------------- */
const extractError = (data) => {
  if (!data) return "";
  if (typeof data === "string") return data;
  if (data.message) return data.message;
  if (data.detail) return data.detail;
  if (data.errors) return Object.values(data.errors).flat().join(" ");
  return "";
};

async function request(path, { method = "GET", body } = {}) {
  try {
    const { data } = await axiosInstance.request({
      url: path,
      method,
      data: body,
      ...(body instanceof FormData && { headers: { "Content-Type": "multipart/form-data" } }),
    });
    return data;
  } catch (err) {
    const data = err?.response?.data;
    const e = new Error(extractError(data) || err.message || "Request failed");
    e.fields = data?.errors || {}; // { industry: "...", slug: "..." }
    throw e;
  }
}

const BASE = "/api/superadmin/blog-page-master/";

export const blogApi = {
  industries: () => request("/api/superadmin/case-study-industries/"),
  list: () => request(BASE),
  get: (slug) => request(`${BASE}${slug}/`),
  create: (fd) => request(BASE, { method: "POST", body: fd }),
  update: (slug, fd) => request(`${BASE}${slug}/`, { method: "PUT", body: fd }),
  remove: (slug) => request(`${BASE}${slug}/`, { method: "DELETE" }),
};

/* ----------------------------- Helpers ----------------------------- */
export const slugify = (s) =>
  s.toLowerCase().trim().replace(/[^a-z0-9\s-]/g, "").replace(/\s+/g, "-").replace(/-+/g, "-").replace(/^-|-$/g, "");

const SLUG_RE = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;

export const Spinner = ({ className = "h-5 w-5" }) => (
  <span className={`inline-block animate-spin rounded-full border-2 border-current border-t-transparent ${className}`} />
);

export function Toast({ toast, onClose }) {
  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(onClose, 3500);
    return () => clearTimeout(t);
  }, [toast, onClose]);
  if (!toast) return null;
  return (
    <div
      role="status"
      className={`fixed right-4 top-4 z-50 max-w-sm rounded-lg px-4 py-3 text-sm text-white shadow-lg ${toast.type === "error" ? "bg-red-600" : "bg-emerald-600"}`}
    >
      {toast.message}
    </div>
  );
}

export const useToast = () => {
  const [toast, setToast] = useState(null);
  const close = useCallback(() => setToast(null), []);
  return [toast, setToast, close];
};


export const useIndustries = () => {
  const [industries, setIndustries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  useEffect(() => {
    let alive = true;
    blogApi
      .industries()
      .then((res) => alive && setIndustries(res.results || []))
      .catch((e) => alive && setError(e.message || "Could not load industries."))
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, []);
  return { industries, loading, error };
};

/* ------------------------------ Form ------------------------------- */
const EMPTY = {
  title: "",
  slug: "",
  industry: "",
  short_description: "",
  content: "",
  author: "",
  is_published: true,
  seo: resolveSeo(EMPTY_SEO),
  schema: EMPTY_SCHEMA,
};

const inputCls = (err) =>
  `w-full rounded-lg border bg-white px-3 py-2 text-sm text-gray-900 focus:outline-none focus:ring-1 ${err ? "border-red-400 focus:border-red-500 focus:ring-red-500" : "border-gray-300 focus:border-indigo-500 focus:ring-indigo-500"
  }`;

function Field({ label, required, hint, error, count, as: Tag = "label", children }) {
  return (
    <Tag className="block">
      <span className="mb-1 flex items-baseline justify-between text-sm font-medium text-gray-700">
        <span>
          {label}
          {required && <span className="text-red-500"> *</span>}
        </span>
        {count}
      </span>
      {children}
      {error ? (
        <span className="mt-1 block text-xs font-medium text-red-600">{error}</span>
      ) : (
        hint && <span className="mt-1 block text-xs text-gray-500">{hint}</span>
      )}
    </Tag>
  );
}

export default function BlogForm({ initial, isEdit, saving, serverErrors, submitLabel, onSubmit, onCancel }) {
  // const [form, setForm] = useState({
  //   ...EMPTY, ...initial,
  //   seo: mergeSeo(initial?.seo),
  //   schema: mergeSchema(initial?.schema),
  // });

  const [form, setForm] = useState(() => {
    const seo = mergeSeo(initial?.seo);
    const base = { ...EMPTY, ...initial };
    if (!seo.canonical_url) seo.canonical_url = canonicalFor(base.slug);
    return { ...base, seo, schema: mergeSchema(initial?.schema) };
  });
  const [canonicalTouched, setCanonicalTouched] = useState(Boolean(mergeSeo(initial?.seo).canonical_url));
  const [ogFile, setOgFile] = useState(null);
  const [twitterFile, setTwitterFile] = useState(null);
  const [ogPreview, setOgPreview] = useState(initial?.og_image || "");
  const [twitterPreview, setTwitterPreview] = useState(initial?.twitter_image || "");

  const [slugTouched, setSlugTouched] = useState(isEdit);
  const [imageFile, setImageFile] = useState(null);
  const [preview, setPreview] = useState(initial?.featured_image || "");
  const [errors, setErrors] = useState({});
  const { industries, loading: industriesLoading, error: industriesError } = useIndustries();

  // Backend validation errors (industry, slug, ...) land on the matching field
  useEffect(() => {
    if (serverErrors && Object.keys(serverErrors).length) setErrors((e) => ({ ...e, ...serverErrors }));
  }, [serverErrors]);

  useEffect(
    () => () => {
      if (preview.startsWith("blob:")) URL.revokeObjectURL(preview);
    },
    [preview]
  );

  const set = (key) => (e) => {
    const value = e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setForm((f) => {
      const next = { ...f, [key]: value };
      if (key === "title" && !slugTouched) next.slug = slugify(value);
      if ((key === "title" || key === "slug") && !canonicalTouched)
        next.seo = { ...f.seo, canonical_url: canonicalFor(next.slug) };
      return next;
    });
    setErrors((er) => ({ ...er, [key]: undefined }));
  };

  const onImage = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Format validation
    const allowedTypes = [
      "image/jpeg",
      "image/jpg",
      "image/png",
      "image/webp",
    ];

    if (!allowedTypes.includes(file.type)) {
      setErrors((er) => ({
        ...er,
        featured_image: "Only JPG, PNG and WEBP images are allowed.",
      }));
      return;
    }

    // Size validation (2 MB)
    const MAX_SIZE = 2 * 1024 * 1024;

    if (file.size > MAX_SIZE) {
      setErrors((er) => ({
        ...er,
        featured_image: "Image size must not exceed 2 MB.",
      }));
      return;
    }

    // Dimension validation
    const img = new Image();

    img.onload = () => {
      if (img.width < 800 || img.height < 500) {
        setErrors((er) => ({
          ...er,
          featured_image:
            "Minimum image size is 800 × 500 px.",
        }));
        return;
      }

      // Optional: show warning for non-recommended size
      if (img.width < 1600 || img.height < 1000) {
        console.warn(
          "Recommended image size is 1600 × 1000 px."
        );
      }

      setImageFile(file);
      setPreview(URL.createObjectURL(file));

      setErrors((er) => ({
        ...er,
        featured_image: undefined,
      }));
    };

    img.onerror = () => {
      setErrors((er) => ({
        ...er,
        featured_image: "Invalid image file.",
      }));
    };

    img.src = URL.createObjectURL(file);
  };

  const pickSocial = (field, setFile, setPrev) => async (file) => {
    const problem = await checkSocialImage(file);
    if (problem) return setErrors((er) => ({ ...er, [field]: problem }));
    setFile(file);
    setPrev(URL.createObjectURL(file));
    setErrors((er) => ({ ...er, [field]: undefined }));
  };

  const validate = () => {
    const er = {};
    if (!form.title.trim()) er.title = "Title is required.";
    else if (form.title.trim().length > 150) er.title = "Title cannot exceed 150 characters.";
    if (!form.slug.trim()) er.slug = "Slug is required.";
    else if (!SLUG_RE.test(form.slug.trim())) er.slug = "Use only lowercase letters, numbers and hyphens.";
    if (!form.industry) er.industry = "Industry is required.";
    if (!form.short_description.trim()) {
      er.short_description = "Short description is required.";
    } else if (form.short_description.trim().length > 500) {
      er.short_description = "Short description cannot exceed 500 characters.";
    }
    if (isEmptyHtml(form.content)) er.content = "Content is required.";
    if (!form.author.trim()) {
      er.author = "Author name is required.";
    } else if (form.author.trim().length > 100) {
      er.author = "Author cannot exceed 100 characters.";
    } else if (!/^[a-zA-Z\s.'-]+$/.test(form.author.trim())) {
      er.author =
        "Author name can contain only letters, spaces, apostrophes, periods and hyphens.";
    }
    if (form.schema.custom_json?.trim()) {
      try {
        JSON.parse(form.schema.custom_json);
      } catch {
        er.schema_custom_json = "Custom schema must be valid JSON.";
      }
    }
    if (!isEdit && !imageFile) er.featured_image = "Featured image is required.";

    setErrors(er);
    return Object.keys(er).length === 0;
  };

  const submit = (e) => {
    e.preventDefault();
    if (!validate()) return;
    const fd = new FormData();
    fd.append("title", form.title.trim());
    fd.append("slug", form.slug.trim());
    fd.append("industry", form.industry);
    fd.append("short_description", form.short_description.trim());
    fd.append("content", form.content.trim());
    fd.append("author", form.author.trim());
    fd.append("is_published", String(form.is_published));
    fd.append("seo", JSON.stringify(form.seo));
    fd.append("schema", JSON.stringify(form.schema));
    if (ogFile) fd.append("og_image", ogFile);
    if (twitterFile) fd.append("twitter_image", twitterFile);
    if (imageFile) fd.append("featured_image", imageFile);
    onSubmit(fd, form);
  };

  return (
    <form   autoComplete="off" onSubmit={submit} noValidate className="space-y-6 rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
      <Field label="Title" required error={errors.title} count={<span className="text-xs text-gray-400">{form.title.length}/150</span>}>
        <input className={inputCls(errors.title)} value={form.title} onChange={set("title")} />
      </Field>

      <div className="grid gap-6 sm:grid-cols-2">
        <Field label="Slug" required hint="Lowercase letters, numbers and hyphens" error={errors.slug}>
          <input
            className={inputCls(errors.slug)}
            value={form.slug}
            onChange={(e) => {
              setSlugTouched(true);
              set("slug")(e);
            }}
          />
        </Field>
        <Field label="Industry" required error={errors.industry || industriesError}>
          <select
            className={inputCls(errors.industry)}
            value={form.industry}
            onChange={set("industry")}
            disabled={industriesLoading}
          >
            <option value="">{industriesLoading ? "Loading industries…" : "Select industry"}</option>
            {industries.map((i) => (
              <option key={i.id} value={i.id}>
                {i.name}
              </option>
            ))}
          </select>
        </Field>
      </div>

      <Field label="Author" error={errors.author} count={<span className="text-xs text-gray-400">{form.author.length}/100</span>}>
        <input className={inputCls(errors.author)} value={form.author} onChange={set("author")} />
      </Field>

      <Field label="Short description" required error={errors.short_description}
        count={
          <span className="text-xs text-gray-400">
            {form.short_description.length}/500
          </span>
        }
      >
        <textarea rows={3} className={inputCls(errors.short_description)} value={form.short_description} onChange={set("short_description")} />
      </Field>

      <Field as="div" label="Content" required error={errors.content}>
        <BlogEditor
          value={form.content}
          hasError={!!errors.content}
          onChange={(html) => {
            setForm((f) => ({ ...f, content: html }));
            setErrors((er) => ({ ...er, content: undefined }));
          }}
        />
      </Field>

      <Field
        label="Featured image"
        required={!isEdit}
        error={errors.featured_image}
        hint={
          isEdit
            ? "JPG, PNG, WEBP • Min 800×500 px • Recommended 1600×1000 px • Max 2 MB"
            : "JPG, PNG, WEBP • Min 800×500 px • Recommended 1600×1000 px • Max 2 MB"
        }
      >
        <div className="flex items-start gap-4">
          {preview ? (
            <img src={preview} alt="Featured preview" className="h-24 w-36 flex-none rounded-lg border border-gray-200 object-cover" />
          ) : (
            <div className="grid h-24 w-36 flex-none place-items-center rounded-lg border border-dashed border-gray-300 bg-gray-50 text-xs text-gray-400">
              No image
            </div>
          )}
          <input
            type="file"
            accept="image/png,image/jpeg,image/webp"
            onChange={onImage}
            className="text-sm text-gray-600 file:mr-3 file:rounded-lg file:border-0 file:bg-gray-100 file:px-3 file:py-2 file:text-sm file:font-medium file:text-gray-700 hover:file:bg-gray-200"
          />
        </div>
      </Field>

      <label className="flex items-center gap-3">
        <input type="checkbox" checked={form.is_published} onChange={set("is_published")} className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500" />
        <span className="text-sm font-medium text-gray-700">Published</span>
      </label>

      <BlogSeoSchema
        seo={form.seo}
        schema={form.schema}
        errors={errors}
        onSeoChange={(seo) => {
          if (seo.canonical_url !== form.seo.canonical_url) setCanonicalTouched(true);
          setForm((f) => ({ ...f, seo }));
        }}
        onSchemaChange={(schema) => {
          setForm((f) => ({ ...f, schema }));
          setErrors((er) => ({ ...er, schema_custom_json: undefined }));
        }}
        ogPreview={ogPreview}
        twitterPreview={twitterPreview}
        featuredPreview={preview}
        onOgImage={pickSocial("og_image", setOgFile, setOgPreview)}
        onTwitterImage={pickSocial("twitter_image", setTwitterFile, setTwitterPreview)}
      />

      <div className="flex justify-end gap-3 border-t border-gray-100 pt-6">
        <button type="button" onClick={onCancel} className="rounded-lg border border-gray-300 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50">
          Cancel
        </button>
        <button type="submit" disabled={saving} className="flex items-center gap-2 rounded-lg bg-indigo-600 px-5 py-2 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-60">
          {saving && <Spinner className="h-4 w-4" />}
          {submitLabel}
        </button>
      </div>
    </form>
  );
}